/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { Fragment, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigState, LibraryData, StraussLogoType, UIState } from '../store/interfaces';
import { translate } from '../i18n';
import { ReactComponent as IconStandard } from '../icons/10_micro_box_no_type_rgb_pos.svg';
import { ReactComponent as IconBlack } from '../icons/10_micro_box_no_type_black_pos.svg';
import { ReactComponent as IconWhite } from '../icons/10_micro_box_no_type_white_border_pos.svg';

import { copyTextToClipboard } from '../helpers/helpers';
import styles from '../styles/currentConfig.module.scss'
import { getURL, GET_LOGO } from '../store/api';

interface CurrentConfigProps {
    config: ConfigState;
    ui: UIState;
    library: LibraryData;
}

export function CurrentConfig(props: CurrentConfigProps) {
    const {t, i18n} = useTranslation();
    const [isCodeCopied, setIsCodeCopied] = useState(false);

    const selectedBaseColor = props.library.baseColors.find((value) => {return value.id === props.config.baseColorId;});
    const selectedBaseColorName = selectedBaseColor ? translate(selectedBaseColor.name, i18n) : '';
    const selectedContrastColor = props.library.contrastColors.find((value) => {return value.id === props.config.contrastColorId;});
    const selectedContrastColorName = selectedContrastColor ? translate(selectedContrastColor.name, i18n) : '';

    let selectedStraussLogoName = '';
    let straussLogoIcon: ReactElement<any>|null = null;
    if(props.config.straussLogo === StraussLogoType.Standard) {
        selectedStraussLogoName = t('logos.standard');
        straussLogoIcon = <IconStandard/>;
    } else if(props.config.straussLogo === StraussLogoType.StitchedBlack) {
        selectedStraussLogoName = t('logos.stitchedBlack');
        straussLogoIcon = <IconBlack/>;
    } else if(props.config.straussLogo === StraussLogoType.StitchedWhite) {
        selectedStraussLogoName = t('logos.stitchedWhite');
        straussLogoIcon = <IconWhite/>;
    }

    return (
        <div className={styles.container}>
            <h1>{t('current.header')}</h1>

            <div className={styles.contentInfo}>
                <div>
                    <span className={styles.infoBold}>{t('editscreen.baseColor')}</span>
                </div>
                <div>
                    <span className={styles.infoBold}>{t('editscreen.contrastColor')}</span>
                </div>
                <div>
                    <span className={styles.infoBold}>{t('editscreen.straussLogo')}</span>
                </div>
            </div>
            
            <div className={styles.contentInfo}>
                <div>
                    <div className={`${styles.contentColor} ${selectedBaseColor?.id === '06' ? styles.colorSwatchBorder : ''}`} style={{backgroundColor: selectedBaseColor ? selectedBaseColor.color : '#ffffff'}}></div><span>{selectedBaseColorName}</span>
                </div>
                <div>
                    <div className={`${styles.contentColor} ${selectedContrastColor?.id === '06' ? styles.colorSwatchBorder : ''}`} style={{backgroundColor: selectedContrastColor ? selectedContrastColor.color : '#ffffff'}}></div><span>{selectedContrastColorName}</span>
                </div>
                <div>
                    {straussLogoIcon} <span>{selectedStraussLogoName}</span>
                </div>
            </div>
            
            {
                props.config.logoFiles.length > 0 && 
                <Fragment>
                    <div className={`${styles.contentInfo} ${styles.contentInfoSingle}`}>
                        <div>
                            <span className={styles.infoBold}>{t('current.uploadedLogo')}</span>
                        </div>
                    </div>
                    
                    {
                        props.config.logoFiles.map((logoFile, logoFileIndex) => {
                            return <div className={`${styles.contentInfo} ${styles.contentInfoSingle}`} key={logoFileIndex}>
                                <div>
                                    {(logoFile.originalFilename.endsWith('.png') || logoFile.originalFilename.endsWith('.jpg') || logoFile.originalFilename.endsWith('.jpeg')) &&
                                        <div className={styles.logoListEntryImage}>
                                            <img src={getURL(GET_LOGO, {
                                                id: props.config.code,
                                                filename: logoFile.filename
                                            })} alt={logoFile.filename}></img>
                                        </div>
                                    }
                                    <span>{logoFile.originalFilename}</span>
                                </div>
                            </div>;
                        })                        
                    }
                </Fragment>
            }
            
            <h2>{t('current.save')}</h2>
            <div className={styles.codeContainer}>
                <input type="text" value={props.ui.code} disabled={true}/>
                <button className={`${styles.buttonSecondary}`} disabled={props.ui.isLoadingConfig} onClick={() => {
                    copyTextToClipboard(props.ui.code)
                    .then(() => {
                        setIsCodeCopied(true);
                        setTimeout(() => {
                            setIsCodeCopied(false);
                        }, 3000);
                    })
                    .catch((err) => {
                        console.log(err);
                    });
                }}>{t('save.copyCode')}</button>
            </div>
            <p className={styles.codeInfo}>{t('save.info')}</p>
            <p className={`${styles.copied} ${isCodeCopied ? styles.copiedVisible : ''}`}>{t('save.copied')}</p>
        </div>
    );
};
